/**
 * JOB HUNTER › JOBS EXPLORER — /job-hunter/jobs
 *
 * Every posting the agent stored, with this candidate's verdict: filtered out
 * (and why), queued for AI, below the bar (with the score) or proposed. Any job
 * can be scored on demand — the agent's filter is a cost gate, not a judgement.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, ExternalLink, Link2, LoaderCircle, RefreshCw, Search, Sparkles } from 'lucide-react';
import { Button } from '@/components/Button';
import { JobHunterShell, useRunFinished } from '@/components/job-hunter/JobHunterShell';
import { ApplicationDrawer } from '@/components/job-hunter/ApplicationDrawer';
import { ImportJobModal } from '@/components/job-hunter/ImportJobModal';
import { AtsBadge, CountryTag, OutcomeBadge, Pill, Segmented, highlight, JhSelect } from '@/components/job-hunter/ui';
import { useJobHunter } from '@/lib/job-hunter/store';
import { BELOW_REASON_LABEL, REMOTE_LABEL, agentHex, timeAgo, flag } from '@/lib/job-hunter/format';
import { jobHunterService, type JhJob, type JhOutcome } from '@/services/job-hunter.service';

type Outcome = 'all' | JhOutcome | 'unscreened';

function JobDetail({ job, profileUid, agentUid, onScored }: { job: JhJob; profileUid: string | null; agentUid?: string; onScored: (applicationUid: string | null) => void }) {
  const [full, setFull] = useState<JhJob | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => { let live = true; jobHunterService.job(job.jobUid).then((r) => { if (live) setFull(r.job); }).catch(() => undefined); return () => { live = false; }; }, [job.jobUid]);
  const score = async () => {
    if (!profileUid) return;
    setBusy('score');
    try {
      const r = await jobHunterService.scoreJob(job.jobUid, profileUid, agentUid);
      toast.success(r.application ? `Scored ${r.application.evaluation.fitScore} — added to matches` : 'Scored');
      onScored(r.application?.applicationUid ?? null);
    } catch { /* toasted */ } finally { setBusy(null); }
  };
  const verify = async () => {
    setBusy('verify');
    try { const r = await jobHunterService.verifyJob(job.jobUid); toast[r.isOpen ? 'success' : 'error'](r.checked ? (r.isOpen ? 'Still open' : 'Closed') : 'Could not reach the posting'); onScored(null); }
    catch { /* toasted */ } finally { setBusy(null); }
  };
  return (
    <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_260px]">
      <div className="max-h-80 overflow-y-auto rounded-xl border border-border bg-surface p-4">
        {full ? <div className="jh-desc text-[12.5px]">{full.descriptionText ? highlight(full.descriptionText.slice(0, 6000), ['visa', 'sponsor', 'relocation', ...(full.sponsorshipHits ?? [])]) : 'No description stored.'}</div>
          : <LoaderCircle className="animate-spin text-primary" size={18} />}
      </div>
      <div className="space-y-2">
        <Button className="w-full" size="sm" loading={busy === 'score'} disabled={!profileUid} iconLeft={<Sparkles size={14} />} onClick={score}>Score now</Button>
        <Button className="w-full" size="sm" variant="secondary" loading={busy === 'verify'} iconLeft={<RefreshCw size={14} />} onClick={verify}>Re-check it's open</Button>
        <a href={job.url} target="_blank" rel="noreferrer" className="block"><Button className="w-full" size="sm" variant="ghost" iconRight={<ExternalLink size={13} />}>Open posting</Button></a>
        {full?.screens?.length ? (
          <div className="rounded-xl border border-border p-3 text-[12px]">
            <div className="mb-1.5 font-semibold">Verdicts</div>
            {full.screens.map((s) => (
              <div key={s.agentUid ?? s.profileUid} className="flex items-center justify-between gap-2 py-0.5">
                <span className="inline-flex min-w-0 items-center gap-1.5 text-text-secondary" title={s.profileName}>
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: agentHex(s.agentColor) }} />
                  <span className="truncate">{s.agentName ?? 'Agent'}{s.profileName ? ` · ${s.profileName}` : ''}</span>
                </span>
                <OutcomeBadge outcome={s.outcome} title={s.reasonLabel} />
              </div>
            ))}
          </div>
        ) : null}
        {full?.seenOn && full.seenOn.length > 1 && <div className="jh-muted">Seen on {full.seenOn.length} boards: {Array.from(new Set(full.seenOn.map((s) => s.sourceType))).join(', ')}</div>}
      </div>
    </div>
  );
}

export default function JhJobs() {
  const { profileUid, meta, agents } = useJobHunter();
  const [agentUid, setAgentUid] = useState('');
  // The verdict column is one agent's: default to the candidate's first agent.
  useEffect(() => { if (agents && !agents.some((a) => a.agentUid === agentUid)) setAgentUid(agents[0]?.agentUid ?? ''); }, [agents, agentUid]);
  const [outcome, setOutcome] = useState<Outcome>('all');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState('');
  const [remote, setRemote] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [open, setOpen] = useState('true');
  const [onlyAts, setOnlyAts] = useState(false);
  const [onlyVisa, setOnlyVisa] = useState(false);
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: JhJob[]; total: number; outcomes: Partial<Record<JhOutcome, number>> } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => { const t = window.setTimeout(() => setQuery(q.trim()), 300); return () => window.clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [outcome, query, country, remote, sourceType, open, onlyAts, onlyVisa, days, agentUid]);

  const load = useCallback(async () => {
    try {
      const r = await jobHunterService.jobs({
        agentUid: agentUid || undefined, outcome: outcome === 'all' || !agentUid ? undefined : outcome, q: query || undefined, country: country || undefined,
        remoteType: remote || undefined, sourceType: sourceType || undefined, open: open || undefined, fromAts: onlyAts ? 'true' : undefined,
        sponsorship: onlyVisa ? 'true' : undefined, days: days || undefined, page, limit: 40,
      });
      setData({ rows: r.rows, total: r.total, outcomes: r.outcomes });
    } catch { setData({ rows: [], total: 0, outcomes: {} }); }
  }, [agentUid, outcome, query, country, remote, sourceType, open, onlyAts, onlyVisa, days, page]);
  useEffect(() => { void load(); }, [load]);
  useRunFinished(load);

  const o = data?.outcomes ?? {};
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / 40));

  return (
    <JobHunterShell section="Jobs" description="Everything the agents found, with each agent's verdict"
      actions={<Button size="sm" variant="secondary" iconLeft={<Link2 size={14} />} onClick={() => setImporting(true)}>Add by link</Button>}>
      <div className="jh-toolbar">
        {(agents?.length ?? 0) > 0 && (
          <JhSelect size="sm" className="w-52" ariaLabel="Verdict of agent" value={agentUid} onChange={setAgentUid}
            options={(agents ?? []).map((a) => ({ value: a.agentUid, label: `Verdict of: ${a.name}`, icon: <span className="h-2.5 w-2.5 rounded-full" style={{ background: agentHex(a.color) }} /> }))} />
        )}
        <Segmented<Outcome> value={outcome} onChange={setOutcome} options={[
          { value: 'all', label: 'All' },
          { value: 'proposed', label: 'Proposed', n: o.proposed ?? 0 },
          { value: 'below_threshold', label: 'Below the bar', n: o.below_threshold ?? 0 },
          { value: 'queued', label: 'Queued', n: o.queued ?? 0 },
          { value: 'rejected', label: 'Filtered out', n: o.rejected ?? 0 },
          { value: 'unscreened', label: 'Not screened' },
        ]} />
        <div className="relative min-w-[200px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input className="jh-input pl-9" placeholder="Search title or employer" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <div className="jh-toolbar">
        <JhSelect size="sm" className="w-44" ariaLabel="Country" value={country} onChange={setCountry} searchPlaceholder="Search country"
          options={[{ value: '', label: 'All countries' }, ...(meta?.countries ?? []).map((c) => ({ value: c.code, label: c.name, icon: <span className="text-[14px] leading-none">{flag(c.code)}</span>, keywords: c.code }))]} />
        <JhSelect size="sm" className="w-36" ariaLabel="Workplace" value={remote} onChange={setRemote}
          options={[{ value: '', label: 'Any workplace' }, { value: 'remote', label: 'Remote' }, { value: 'hybrid', label: 'Hybrid' }, { value: 'onsite', label: 'On-site' }]} />
        <JhSelect size="sm" className="w-40" ariaLabel="Source" value={sourceType} onChange={setSourceType}
          options={[{ value: '', label: 'Any source' }, ...(meta?.catalog ?? []).map((c) => ({ value: c.type as string, label: c.label })), { value: 'manual', label: 'Added by link' }]} />
        <JhSelect size="sm" className="w-40" ariaLabel="Open" value={open} onChange={setOpen}
          options={[{ value: 'true', label: 'Open only' }, { value: 'false', label: 'Closed only' }, { value: '', label: 'Open and closed' }]} />
        <JhSelect size="sm" className="w-36" ariaLabel="First seen" value={days} onChange={setDays}
          options={[1, 3, 7, 14, 30, 90, 0].map((d) => ({ value: d, label: d ? `Last ${d} day${d === 1 ? '' : 's'}` : 'Any time' }))} />
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-text-secondary"><input type="checkbox" className="accent-primary" checked={onlyAts} onChange={(e) => setOnlyAts(e.target.checked)} />Employer ATS only</label>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-text-secondary"><input type="checkbox" className="accent-primary" checked={onlyVisa} onChange={(e) => setOnlyVisa(e.target.checked)} />Mentions visa / relocation</label>
        <span className="ml-auto text-xs text-text-muted">{data ? `${data.total.toLocaleString()} jobs` : ''}</span>
      </div>

      <div className="jh-card overflow-x-auto">
        <table className="jh-table min-w-[880px]">
          <thead><tr><th style={{ width: 28 }} /><th>Job</th><th>Location</th><th>Source</th><th>Seen</th><th>Verdict</th></tr></thead>
          <tbody>
            {data === null ? Array.from({ length: 8 }).map((_, i) => <tr key={i}><td colSpan={6}><div className="h-6 animate-pulse rounded bg-surface-alt" /></td></tr>)
              : data.rows.length === 0 ? <tr><td colSpan={6} className="py-12 text-center jh-muted">No jobs match these filters. Load sources and run the agent to fill this list.</td></tr>
              : data.rows.map((j) => {
                const isOpen = expanded === j.jobUid;
                const s = j.screen;
                const verdictTitle = s?.reasonLabel ?? (s?.reason ? BELOW_REASON_LABEL[s.reason] ?? s.reason : undefined);
                return (
                  <Fragment key={j.jobUid}>
                    <tr className="cursor-pointer" onClick={() => setExpanded(isOpen ? null : j.jobUid)}>
                      <td>{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                      <td className="max-w-[420px]">
                        <div className="truncate font-semibold">{j.title}</div>
                        <div className="mt-0.5 flex items-center gap-2 truncate text-[11.5px] text-text-muted">{j.employerName}{!j.isOpen && <Pill tone="danger">Closed</Pill>}{j.sponsorshipHits?.length ? <Pill tone="success">mentions {j.sponsorshipHits[0]}</Pill> : null}{j.flags.includes('agency') && <Pill tone="warning">Agency</Pill>}{j.flags.includes('template_spam') && <Pill tone="danger">Template spam</Pill>}</div>
                      </td>
                      <td><div className="flex flex-col gap-0.5"><CountryTag cc={j.country} city={j.city} compact />{j.remoteType !== 'unknown' && <span className="text-[11px] text-text-muted">{REMOTE_LABEL[j.remoteType]}</span>}</div></td>
                      <td><AtsBadge fromAts={j.fromAts} type={j.sourceType} /></td>
                      <td className="whitespace-nowrap text-text-muted">{timeAgo(j.postedAt ?? j.firstSeenAt)}</td>
                      <td>
                        <div className="flex flex-col items-start gap-0.5">
                          <OutcomeBadge outcome={s?.outcome ?? 'unscreened'} title={verdictTitle} />
                          {s?.score !== undefined && <span className="text-[11px] font-semibold tabular-nums text-text-secondary">fit {s.score}</span>}
                          {verdictTitle && s?.outcome !== 'proposed' && <span className="max-w-[200px] truncate text-[11px] text-text-muted" title={verdictTitle}>{verdictTitle}</span>}
                        </div>
                      </td>
                    </tr>
                    {isOpen && <tr><td colSpan={6} className="!bg-surface-alt/50 !p-0"><JobDetail job={j} profileUid={profileUid} agentUid={agentUid || undefined} onScored={(uid) => { void load(); if (uid) setDrawer(uid); }} /></td></tr>}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2 text-xs">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-text-muted">Page {page} of {pages}</span>
          <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
      <ApplicationDrawer applicationUid={drawer} onClose={() => setDrawer(null)} onChanged={() => void load()} />
      <ImportJobModal open={importing} onClose={() => setImporting(false)} profileUid={profileUid} agentUid={agentUid || undefined} onImported={(a) => { void load(); if (a) setDrawer(a.applicationUid); }} />
    </JobHunterShell>
  );
}
